import { VisuallyHidden } from "@react-aria/visually-hidden";
import React, { SVGProps } from "react";

import { assertNever } from "../../utils/never";

export const svgSocialMedia = [
    "envelope",
    "facebook",
    "instagram",
    "link",
    "linkedin",
    "pinterest",
    "twitter",
    "tiktok",
    "twitter-bird",
    "youtube",
] as const;

/** The "All SVGs" story uses this to stay automatically updated */
export const svgNames = [
    ...svgSocialMedia,
    "arrow",
    "download",
    "external",
    "caret",
    "plus",
    "minus",
    "x",
    "burger",
] as const;

export type SVGName = (typeof svgNames)[number];

export interface Props<T extends string> extends SVGProps<SVGSVGElement> {
    name: T;
    /**
     * If a value is provided, it will drop a VisuallyHidden span with the
     * string into the DOM and mark the SVG as `aria-hidden`.
     *
     * If this is null, then you should title the SVG in a different way.
     *
     * If for some reason you need to work around all of this, make this `null`
     * and add `aria-hidden="true"` to `<Svg>`. It will override everything.
     */
    visuallyHiddenText?: string | null;
}

/**
 * This component's primary purpose is to reduce boilerplate:
 * - no need to repeatedly type xmlns
 * - VisuallyHidden text paired with it for free
 */
export const SvgElement = ({
    children,
    visuallyHiddenText,
    ...props
}: Omit<Props<SVGName>, "name"> & { children: React.ReactNode }) => (
    <>
        {!!visuallyHiddenText && (
            <VisuallyHidden elementType="span">
                {visuallyHiddenText}
            </VisuallyHidden>
        )}
        <svg
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden={!!visuallyHiddenText}
            {...props}
        >
            {children}
        </svg>
    </>
);

/**
 * This repo's solution for using icon-like* SVGs.
 *
 * To add to this:
 * - Add a name to `svgNames`
 * - Make the SVG TSX-friendly. https://react-svgr.com/playground/?typescript=true
 *   can help with this
 * - Add a case with your name and your element, replacing `<svg>` with `<SvgElement>`.
 *   You should only need `{...props}` and the `viewBox` attribute; height/width and
 *   fill color can be set with CSS
 *
 * * "icon-like", for our purposes, means that the SVG is a relatively small and simple
 * graphic. Most, if not all of our icons are monochromatic, their fills are modified
 * with CSS, and they might be reused in many different contexts around the site.
 *
 * Some images might be in the SVG format but not fit this mold, and can be treated as
 * static images instead (placed in the static folder and loaded with `<img>`).
 */
export const Svg = ({ name, ...props }: Props<SVGName>) => {
    switch (name) {
        case "facebook": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="M5,3H19a2,2,0,0,1,2,2V19a2,2,0,0,1-2,2H5a2,2,0,0,1-2-2V5A2,2,0,0,1,5,3M18,5H15.5A3.5,3.5,0,0,0,12,8.5V11H10v3h2v7h3V14h3V11H15V9a1,1,0,0,1,1-1h2Z" />
                </SvgElement>
            );
        }
        case "twitter": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="M13.49,10.67l6.19-7H18.19L12.83,9.76,8.53,3.65H3.66L10.1,12.9,3.66,20.25H5.15l5.69-6.44,4.55,6.44h4.95l-6.85-9.58Zm-2,2.23L10.84,12,5.64,4.72H7.87l4.21,5.87.66.91,5.45,7.68H16L11.5,12.9Z" />
                </SvgElement>
            );
        }
        case "twitter-bird": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z" />
                </SvgElement>
            );
        }
        case "youtube": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="M10,15l5.19-3L10,9v6M21.56,7.17a10.34,10.34,0,0,1,.28,1.9c.07.8.1,1.49.1,2.09L22,12a19.87,19.87,0,0,1-.44,4.83,2.34,2.34,0,0,1-1.73,1.73,14.1,14.1,0,0,1-2.65.28c-1.3.07-2.49.1-3.59.1L12,19a40.93,40.93,0,0,1-7.83-.44,2.34,2.34,0,0,1-1.73-1.73,10.34,10.34,0,0,1-.28-1.9c-.07-.8-.1-1.49-.1-2.09L2,12a19.87,19.87,0,0,1,.44-4.83A2.34,2.34,0,0,1,4.17,5.44a14.1,14.1,0,0,1,2.65-.28c1.3-.07,2.49-.1,3.59-.1L12,5a40.93,40.93,0,0,1,7.83.44A2.34,2.34,0,0,1,21.56,7.17Z" />
                </SvgElement>
            );
        }
        case "linkedin": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="M19,3a2,2,0,0,1,2,2V19a2,2,0,0,1-2,2H5a2,2,0,0,1-2-2V5A2,2,0,0,1,5,3H19m-.5,15.5V13.2a3.26,3.26,0,0,0-3.26-3.26h0a2.9,2.9,0,0,0-2.32,1.3V10.13H10.13V18.5h2.79V13.57a1.4,1.4,0,1,1,2.79,0V18.5H18.5M6.88,8.56A1.68,1.68,0,0,0,8.56,6.88h0a1.69,1.69,0,1,0-3.37,0h0A1.69,1.69,0,0,0,6.88,8.56M8.27,18.5V10.13H5.5V18.5Z" />
                </SvgElement>
            );
        }
        case "tiktok": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="M17.85,6.85l-.35-.21a5.16,5.16,0,0,1-.91-.77,4.38,4.38,0,0,1-1-2.12h0a2.28,2.28,0,0,1,0-.75H12.44V14.94c0,.16,0,.32,0,.48v.08h0a2.61,2.61,0,1,1-2.6-2.83,2.73,2.73,0,0,1,.8.13V9.67A5.74,5.74,0,0,0,6.21,11a6.14,6.14,0,0,0-1.32,1.63,5.65,5.65,0,0,0-.68,2.59,6,6,0,0,0,.33,2.05h0a6.28,6.28,0,0,0,.84,1.53A6.17,6.17,0,0,0,6.72,20h0A5.73,5.73,0,0,0,9.88,21a5.52,5.52,0,0,0,2.34-.52,5.66,5.66,0,0,0,2.93-3.14,6.54,6.54,0,0,0,.38-2V9l.53.35a7,7,0,0,0,1.84.77,10.64,10.64,0,0,0,1.89.25V7.32A4.05,4.05,0,0,1,17.85,6.85Z" />
                </SvgElement>
            );
        }
        case "instagram": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="M16.8,6.11a1.08,1.08,0,1,0,1.08,1.08A1.08,1.08,0,0,0,16.8,6.11Zm4.14,2.18a6.81,6.81,0,0,0-.42-2.19,4.31,4.31,0,0,0-1-1.59,4.2,4.2,0,0,0-1.59-1,6.56,6.56,0,0,0-2.19-.43c-.95,0-1.25,0-3.7,0s-2.75,0-3.7,0a6.56,6.56,0,0,0-2.19.43,4.28,4.28,0,0,0-1.59,1,4.31,4.31,0,0,0-1,1.59,6.81,6.81,0,0,0-.42,2.19C3,9.24,3,9.55,3,12s0,2.75.05,3.71a6.8,6.8,0,0,0,.42,2.18,4.23,4.23,0,0,0,1,1.59,4.43,4.43,0,0,0,1.59,1,6.81,6.81,0,0,0,2.19.42C9.25,21,9.55,21,12,21s2.75,0,3.7-.05a6.81,6.81,0,0,0,2.19-.42,4.64,4.64,0,0,0,2.63-2.63,6.8,6.8,0,0,0,.42-2.18c0-1,0-1.26,0-3.71S21,9.24,20.94,8.29Zm-1.62,7.3A5,5,0,0,1,19,17.26a2.63,2.63,0,0,1-.67,1,2.94,2.94,0,0,1-1,.68,5.08,5.08,0,0,1-1.68.3c-.9.05-1.23.06-3.59.06s-2.7,0-3.6-.06A5.23,5.23,0,0,1,6.69,19a3,3,0,0,1-1-.68,2.7,2.7,0,0,1-.67-1,5.08,5.08,0,0,1-.36-1.71c0-.9,0-1.23,0-3.6s0-2.7,0-3.6A4.71,4.71,0,0,1,5,6.69a2.58,2.58,0,0,1,.71-1,2.77,2.77,0,0,1,1-.72A5.27,5.27,0,0,1,8.4,4.67c.9,0,1.24,0,3.6,0s2.7,0,3.6,0A5,5,0,0,1,17.27,5a2.78,2.78,0,0,1,1.07.72,2.72,2.72,0,0,1,.67,1,4.92,4.92,0,0,1,.31,1.7c0,.9.05,1.24.05,3.6S19.36,14.69,19.32,15.59ZM12,7.38A4.61,4.61,0,1,0,16.62,12,4.6,4.6,0,0,0,12,7.38ZM12,15a3,3,0,1,1,3-3h0A3,3,0,0,1,12,15Z" />
                </SvgElement>
            );
        }
        case "pinterest": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="M12 2.5c-5.243 0-9.5 4.25-9.5 9.5 0 4.016 2.504 7.453 6.041 8.836-.103-.75-.156-1.906.031-2.727.173-.765 1.112-4.718 1.112-4.718s-.282-.57-.282-1.391c0-1.336.767-2.32 1.722-2.32.813 0 1.205.609 1.205 1.336 0 .812-.517 2.031-.79 3.164-.228.945.461 1.718 1.408 1.718 1.69 0 2.99-1.78 2.99-4.343 0-2.274-1.636-3.86-3.976-3.86-2.708 0-4.296 2.024-4.296 4.118 0 .812.313 1.687.704 2.164.068.093.068.18.068.273-.076.297-.24.945-.271 1.078-.04.172-.141.235-.322.125-1.188-.555-1.932-2.265-1.932-3.68 0-2.992 2.175-5.742 6.284-5.742 3.294 0 5.86 2.344 5.86 5.485 0 3.273-2.065 5.906-4.93 5.906-.962 0-1.87-.5-2.175-1.094l-.594 2.258c-.212.828-.79 1.86-1.182 2.492A9.577 9.577 0 0 0 12 21.5c5.243 0 9.5-4.25 9.5-9.5A9.49 9.49 0 0 0 12 2.5Z" />
                </SvgElement>
            );
        }
        case "arrow": {
            return (
                <SvgElement {...props} viewBox="0 0 17 14">
                    <polygon points="10.3,0.9 9,2.2 12.9,6.2 0,6.2 0,8.1 12.9,8.1 9,12 10.3,13.3 16.4,7.1 " />
                </SvgElement>
            );
        }
        case "download": {
            return (
                <SvgElement {...props} viewBox="0 0 16 16">
                    <path d="M12.5,5.3L8.9,8.9V0.5H7.2v8.4L3.6,5.3L2.5,6.5l5.6,5.6l5.5-5.6L12.5,5.3z M3.5,13.8h9.2v1.7H3.5V13.8z" />
                </SvgElement>
            );
        }
        case "external": {
            return (
                <SvgElement {...props} viewBox="0 0 16 16">
                    <path d="M8.4,3.7V3H2.3C2.2,3,1.9,3.2,1.9,3.3v10.6c0,0.2,0.2,0.3,0.3,0.3h10.6c0.2,0,0.3-0.2,0.3-0.3v-6h-0.7v5.7H2.7V3.7H8.4z M14.6,1.7h-3.9v0.8h2.6L7.6,8.1l0.6,0.4L13.8,3v2.6h0.8V1.7z" />
                </SvgElement>
            );
        }
        case "caret": {
            return (
                <SvgElement {...props} viewBox="0 0 490 490">
                    <path d="M410.312 454.729 151.767 244.996 410.312 35.271 381.693 0 79.688 244.996 381.693 490z" />
                </SvgElement>
            );
        }
        case "plus": {
            return (
                <SvgElement {...props} viewBox="0 0 20 21">
                    <path d="M17.75 9.5c0 .314-.233.569-.519.569h-7.69v8.074c0 .3-.242.544-.541.544a.544.544 0 0 1-.542-.544v-8.074H.768c-.285 0-.518-.255-.518-.569 0-.314.233-.569.519-.569h7.69V.857c0-.3.242-.545.541-.545.299 0 .542.245.542.545v8.074h7.69c.285 0 .518.255.518.569" />
                </SvgElement>
            );
        }
        case "minus": {
            return (
                <SvgElement {...props} viewBox="0 0 20 2">
                    <path d="M17.75 1.418c0 .316-.233.574-.519.574H.77c-.286 0-.519-.258-.519-.574 0-.316.233-.574.519-.574H17.23c.286 0 .519.258.519.574" />
                </SvgElement>
            );
        }
        case "x": {
            return (
                <SvgElement {...props} viewBox="0 0 490 490">
                    <polygon points="434.5,88 402,55.5 245,212.5 88,55.5 55.5,88 212.5,245 55.5,402 88,434.5 245,277.5 402,434.5 434.5,402 277.5,245 " />
                </SvgElement>
            );
        }
        case "burger": {
            return (
                <SvgElement {...props} viewBox="0 0 489.6 380.8">
                    <path d="M489.6,46h-490V0h490V46z M489.8,167.4h-490v46h490V167.4z M489.8,334.8h-490v46h490V334.8z" />
                </SvgElement>
            );
        }
        case "envelope": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="M 2.5959066,4.9165 H 21.421334 l -9.225501,7.025124 z M 9.012717,11.33328 2,6.1998893 V 18.200067 Z M 22,6.1998893 V 18.175067 l -6.65525,-6.900122 z m -7.668167,5.8501537 6.7915,7.033457 H 2.902175 l 7.140366,-6.991791 2.161709,1.583363 z" />
                </SvgElement>
            );
        }
        case "link": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="m 13.045904,7.9201843 2.391842,-2.394363 c 0.834841,-0.8356842 2.185473,-0.8356842 3.029674,0 0.834841,0.8356842 0.834841,2.1877957 0,3.0328586 l -4.033355,4.0376041 c -0.83475,0.835621 -2.185472,0.835621 -3.020313,0 -0.37521,-0.375602 -0.975511,-0.375602 -1.341307,0 -0.3658153,0.375511 -0.3751933,0.976527 0,1.34267 1.575847,1.577454 4.13653,1.577454 5.712342,0 l 4.033354,-4.0375503 c 1.575812,-1.5774635 1.575812,-4.1408427 0,-5.7183062 -1.575811,-1.5774633 -4.136494,-1.5774633 -5.712306,0 l -3.320553,3.3239431 c 0.76923,-0.046948 1.557092,0.093896 2.269982,0.4225405 z M 5.532578,18.474191 c -0.8348138,-0.835711 -0.8348138,-2.18784 0,-3.032832 L 7.5492553,13.422535 9.5659325,11.4038 c 0.8347785,-0.835712 2.1855005,-0.835712 3.0203415,0 0.37521,0.375511 0.97551,0.375511 1.34127,0 0.375212,-0.375601 0.375212,-0.976528 0,-1.342768 -1.575811,-1.5774634 -4.1364942,-1.5774634 -5.712324,0 l -2.0166773,2.018744 -2.0166771,2.018823 c -1.5758208,1.577455 -1.5758208,4.140808 0,5.718262 1.5758297,1.577545 4.1365395,1.577545 5.7123602,0 l 2.0166872,-2.018735 1.303831,-1.30519 c -0.76914,0.04693 -1.557091,-0.09388 -2.269982,-0.42254 l -0.375211,0.375601 -2.0166506,2.018823 c -0.8348139,0.835623 -2.1855175,0.835623 -3.0297093,0 z" />
                </SvgElement>
            );
        }
    }
    // Make sure we didn't forget to handle any icons
    assertNever(name);
};
